import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link as MuiLink,
  MenuItem,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  salesOrdersApi,
  type CreateSalesOrderRequest
} from '@/api/modules/salesOrders';
import { quotationsApi, type QuotationSummary } from '@/api/modules/quotations';
import { customersApi, type CustomerSummary } from '@/api/modules/customers';
import { ApiException } from '@/api/types';
import { paths } from '@/router/paths';

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'SGD'];

interface FormState {
  customerId: string;
  quotationId?: string;
  totalAmount: string;
  currency: string;
}

/**
 * Post-Wave-1 sales-order create.
 *
 * Two-picker flow:
 *   1. Customer (required) — same Autocomplete as QuotationCreatePage.
 *   2. From quotation (optional) — filters to quotations for the selected
 *      customer once one is chosen; prefills the total + currency.
 *
 * ?quotationId=<uuid> query param prefills both pickers.
 */
export function SalesOrderCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuotationId = searchParams.get('quotationId') ?? '';

  const prefillQuotation = useQuery({
    queryKey: ['quotation', initialQuotationId],
    queryFn: () => quotationsApi.get(initialQuotationId),
    enabled: Boolean(initialQuotationId)
  });

  const customerOptions = useQuery({
    queryKey: ['customers', 'summaries', 0],
    queryFn: () => customersApi.summaries(0, 50)
  });

  const quotationOptions = useQuery({
    queryKey: ['quotations', 'summaries', 0],
    queryFn: () => quotationsApi.summaries(0, 50)
  });

  const [form, setForm] = useState<FormState>({
    customerId: '',
    quotationId: initialQuotationId || undefined,
    totalAmount: '',
    currency: 'INR'
  });

  // Backfill the form once the prefill quotation lands — but only if the user
  // hasn't started editing yet (customerId empty means untouched).
  const prefilled = prefillQuotation.data;
  if (prefilled && form.customerId === '' && form.totalAmount === '') {
    setForm({
      customerId: prefilled.customerId ?? '',
      quotationId: prefilled.id,
      totalAmount: prefilled.totalAmount,
      currency: prefilled.currency
    });
  }

  const selectedCustomer =
    customerOptions.data?.items.find((c) => c.id === form.customerId) ?? null;
  const filteredQuotations =
    (quotationOptions.data?.items ?? []).filter((q) => {
      if (!selectedCustomer) return true;
      // Cheap heuristic — the summary payload includes customerCode, so match
      // on that. Server-side once we add quotation-by-customer filtering,
      // switch to a query param.
      return q.customerCode === selectedCustomer.code;
    });
  const selectedQuotation =
    (quotationOptions.data?.items ?? []).find((q) => q.id === form.quotationId) ?? null;

  const mutation = useMutation({
    mutationFn: (req: CreateSalesOrderRequest) => salesOrdersApi.create(req),
    onSuccess: (created) => {
      navigate(paths.salesOrderDetail.replace(':id', created.id), { replace: true });
    }
  });

  const fieldErrors = extractFieldErrors(mutation.error);
  const topLevelError =
    mutation.error && !(mutation.error instanceof ApiException)
      ? (mutation.error as Error).message
      : mutation.error instanceof ApiException && !mutation.error.error.fieldErrors?.length
        ? mutation.error.error.message
        : null;

  const handleChange =
    (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.customerId) return;
    mutation.mutate({
      customerId: form.customerId,
      quotationId: form.quotationId?.trim() ? form.quotationId : undefined,
      totalAmount: form.totalAmount,
      currency: form.currency
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h1" gutterBottom>
        New sales order
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Code is generated automatically (<code>SO-YYYY-000001</code>). Starts
        in <b>DRAFT</b>. Optionally link to the accepted quotation it was
        raised from.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={customerOptions.data?.items ?? []}
                loading={customerOptions.isLoading}
                getOptionLabel={(c) => `${c.code} · ${c.name}`}
                value={selectedCustomer}
                onChange={(_, selected: CustomerSummary | null) => {
                  setForm((f) => ({
                    ...f,
                    customerId: selected?.id ?? '',
                    // Clear the quotation link if the customer changes — likely stale.
                    quotationId: undefined,
                    currency: selected?.currency ?? f.currency
                  }));
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Customer"
                    error={Boolean(fieldErrors.customerId)}
                    helperText={fieldErrors.customerId ?? (
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={() => navigate(paths.customerCreate)}
                        underline="hover"
                      >
                        + New customer
                      </MuiLink>
                    )}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {customerOptions.isLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Autocomplete
                options={filteredQuotations}
                loading={quotationOptions.isLoading}
                getOptionLabel={(q) => `${q.code} · ${q.currency} ${q.totalAmount}`}
                value={selectedQuotation}
                disabled={!selectedCustomer}
                onChange={(_, selected: QuotationSummary | null) => {
                  setForm((f) => ({
                    ...f,
                    quotationId: selected?.id,
                    totalAmount: selected?.totalAmount ?? f.totalAmount,
                    currency: selected?.currency ?? f.currency
                  }));
                }}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="From quotation (optional)"
                    helperText={
                      fieldErrors.quotationId ??
                      (selectedCustomer
                        ? 'Selecting a quotation prefills total + currency.'
                        : 'Pick a customer first.')
                    }
                    error={Boolean(fieldErrors.quotationId)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {quotationOptions.isLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                required
                label="Total amount"
                helperText={fieldErrors.totalAmount ?? 'e.g. 12500.00'}
                error={Boolean(fieldErrors.totalAmount)}
                value={form.totalAmount}
                onChange={handleChange('totalAmount')}
                inputProps={{ inputMode: 'decimal' }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                required
                select
                label="Currency"
                error={Boolean(fieldErrors.currency)}
                helperText={fieldErrors.currency ?? undefined}
                value={form.currency}
                onChange={handleChange('currency')}
              >
                {CURRENCIES.map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {topLevelError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {topLevelError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          type="button"
          variant="text"
          onClick={() => navigate(paths.salesOrders)}
          disabled={mutation.isPending}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          disabled={mutation.isPending || !form.customerId}
        >
          {mutation.isPending ? 'Creating…' : 'Create sales order'}
        </Button>
      </Stack>
    </Box>
  );
}

function extractFieldErrors(err: unknown): Record<string, string | undefined> {
  if (!(err instanceof ApiException)) return {};
  const fields = err.error.fieldErrors ?? [];
  const out: Record<string, string> = {};
  for (const fe of fields) out[fe.field] = fe.message;
  return out;
}
